import { useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';

import { useUrlFilters } from '@/hooks/useUrlFilters';
import type {
  MonthOption,
  ParentAttendanceMark,
  ParentAttendanceRecord,
} from '../../types/attendance';
import { ATTENDANCE_MARK_LABEL, AttendanceStatusPill } from './AttendanceStatusPill';

export interface AttendanceHistoryCardProps {
  records: ParentAttendanceRecord[];
  /** Every month of the session up to this one, newest first. */
  months: MonthOption[];
  selectedMonthKey: string;
  onMonthChange: (monthKey: string) => void;
}

const PAGE_SIZE = 10;

type StatusFilter = 'ALL' | ParentAttendanceMark;

const STATUS_FILTERS: StatusFilter[] = ['ALL', 'PRESENT', 'ABSENT', 'LATE', 'LEAVE'];

/** The table's filter and page, as they sit in the address bar. */
type Filters = {
  page: number;
  status: StatusFilter;
};

const DEFAULT_FILTERS: Filters = { page: 1, status: 'ALL' };

/** The report tab's table pages too, so this one keeps to its own prefix. */
const FILTER_OPTIONS = {
  prefix: 'history',
  allowed: { status: STATUS_FILTERS },
};

export function AttendanceHistoryCard({
  records,
  months,
  selectedMonthKey,
  onMonthChange,
}: AttendanceHistoryCardProps) {
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const statusFilter = filters.status;

  const currentMonth =
    months.find((m) => m.key === selectedMonthKey) ??
    months[0];

  // Filter records
  const filteredRecords = useMemo(() => {
    if (statusFilter === 'ALL') return records;
    return records.filter((rec) => rec.status === statusFilter);
  }, [records, statusFilter]);

  const totalRecords = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  // A page restored from the address may be past the end of a shorter month.
  const currentPage = Math.min(filters.page, totalPages);

  // Current page records
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredRecords.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredRecords, currentPage]);

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(currentPage * PAGE_SIZE, totalRecords);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setFilters({ page });
    }
  };

  return (
    <div className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 flex flex-col justify-between shadow-xs">
      {/* Card Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
        <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-white">
          Attendance History
        </h2>

        {/* Month Selector + Filter Button */}
        <div className="flex items-center gap-2">
          {/* Month Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setIsMonthDropdownOpen((prev) => !prev);
                setIsFilterDropdownOpen(false);
              }}
              className="flex h-8 items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2.5 sm:px-3 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              <span className="text-sm">{currentMonth.label}</span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>

            {isMonthDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setIsMonthDropdownOpen(false)}
                />
                <div className="absolute right-0 mt-1 w-44 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 shadow-xl z-30">
                  {months.map((option: MonthOption) => {
                    const isSelected = option.key === selectedMonthKey;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => {
                          onMonthChange(option.key);
                          setFilters({ page: 1 });
                          setIsMonthDropdownOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-600 text-white font-bold'
                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <span>{option.label}</span>
                        {isSelected && <span>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Filter Button */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setIsFilterDropdownOpen((prev) => !prev);
                setIsMonthDropdownOpen(false);
              }}
              className={`flex h-8 items-center gap-1.5 rounded-xl border px-2.5 sm:px-3 text-xs font-bold transition-colors cursor-pointer ${
                statusFilter !== 'ALL'
                  ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Filter className="h-3.5 w-3.5" />
              <span className="text-sm">Filter</span>
            </button>

            {isFilterDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setIsFilterDropdownOpen(false)}
                />
                <div className="absolute right-0 mt-1 w-36 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 shadow-xl z-30">
                  {STATUS_FILTERS.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => {
                        setFilters({ status, page: 1 });
                        setIsFilterDropdownOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3.5 py-1.5 text-sm font-medium capitalize transition-colors cursor-pointer ${
                        statusFilter === status
                          ? 'bg-indigo-600 text-white font-bold'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <span>{status === 'ALL' ? 'All Status' : ATTENDANCE_MARK_LABEL[status]}</span>
                      {statusFilter === status && <span>✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Clear Filters Button */}
          {statusFilter !== 'ALL' && (
            <button
              type="button"
              onClick={() => setFilters({ status: 'ALL', page: 1 })}
              title="Clear status filter"
              className="flex h-8 items-center gap-1 rounded-xl border border-rose-500/40 bg-rose-500/10 px-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 transition-colors cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Attendance Records Table */}
      <div className="overflow-x-auto min-h-[360px]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-800 text-xs font-bold text-slate-500 dark:text-slate-300">
              <th className="py-2.5 px-3 text-sm">Date</th>
              <th className="py-2.5 px-3 text-sm">Day</th>
              <th className="py-2.5 px-3 text-sm">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs sm:text-sm">
            {paginatedRecords.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-12 text-center text-xs text-slate-400">
                  <p>No attendance records found for this filter.</p>
                  {statusFilter !== 'ALL' && (
                    <button
                      type="button"
                      onClick={() => setFilters({ status: 'ALL', page: 1 })}
                      className="mt-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                    >
                      Clear filter
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              paginatedRecords.map((record) => (
                <tr
                  key={record.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="py-2.5 px-3 font-semibold text-slate-900 dark:text-slate-100">
                    {record.formattedDate}
                  </td>
                  <td className="py-2.5 px-3 text-slate-600 dark:text-slate-400 font-medium">{record.day}</td>
                  <td className="py-2.5 px-3">
                    <AttendanceStatusPill status={record.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs font-medium text-slate-500 dark:text-slate-400">
        <div>
          Showing {startRecord} to {endRecord} of {totalRecords} records
        </div>

        {/* Pagination Buttons */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
            const isActive = pageNum === currentPage;
            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => handlePageChange(pageNum)}
                className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
